import Button from 'components/button';
import Calendar from 'components/crm/ui/Calendar';
import { CalendarClock } from 'lucide-react';
import { dayLabel, hourLabel, parseClock } from 'helper/timeLabels';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';
import { defaultCustomSlot, scheduleSlots } from './scheduleSlots';

const pad = (value) => String(value).padStart(2, '0');
export const whenLabel = (date) => `${dayLabel(date)} · ${hourLabel(date)}`;

/**
 * « Programmer l'envoi », over the footer of the window: the quick times
 * (tomorrow morning, tomorrow afternoon, Monday morning), then « Choisir une
 * date et une heure » with a calendar and a time. `onPick(date)` programs the
 * e-mail; a click outside or Escape closes it.
 */
const SchedulePopover = ({ onPick, onClose }) => {
  const { text } = useText('mailComposer');
  const box = useRef(null);
  const [now] = useState(() => new Date());
  const [custom, setCustom] = useState(null);

  useEffect(() => {
    const outside = (event) => box.current && !box.current.contains(event.target) && onClose();
    const escape = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [onClose]);

  const openCustom = () => {
    const start = defaultCustomSlot(now);
    setCustom({ day: start, time: `${pad(start.getHours())}:${pad(start.getMinutes())}` });
  };
  const clock = custom && parseClock(custom.time);
  const chosen =
    custom && clock ? new Date(custom.day.getFullYear(), custom.day.getMonth(), custom.day.getDate(), clock[0], clock[1]) : null;
  const past = Boolean(chosen) && chosen <= new Date();

  return (
    <div
      ref={box}
      role="dialog"
      aria-label={text('scheduleTitle')}
      className="absolute bottom-full left-4 z-20 mb-2 rounded-xl border border-gray-100 bg-white p-1 shadow-lg motion-safe:animate-menuIn"
    >
      {!custom ? (
        <div className="w-80">
          <p className="px-3 pb-1 pt-2 text-xs font-medium text-gray-500">{text('scheduleTitle')}</p>
          {scheduleSlots(now).map((slot) => (
            <button
              key={slot.id}
              type="button"
              onClick={() => onPick(slot.at)}
              className="flex w-full items-center justify-between gap-4 rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
            >
              <span>{text(`slot_${slot.id}`)}</span>
              <span className="text-xs text-gray-500">{whenLabel(slot.at)}</span>
            </button>
          ))}
          <div className="my-1 border-b border-gray-100" />
          <button
            type="button"
            onClick={openCustom}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
          >
            <CalendarClock size={18} strokeWidth={2} className="text-gray-500" />
            {text('slot_custom')}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3 p-3">
          <p className="text-sm font-medium text-gray-900">{text('slot_custom')}</p>
          {/* The calendar is drawn as a popover of its own: here it sits in one. */}
          <div className="[&>div]:border-0 [&>div]:px-1 [&>div]:pt-1 [&>div]:shadow-none">
            <Calendar value={custom.day} onChange={(day) => setCustom((current) => ({ ...current, day }))} />
          </div>
          <label className="flex items-center justify-between gap-4 px-1 text-sm text-gray-700">
            <span>
              {text('timeLabel')} · <span className="text-gray-500">{dayLabel(custom.day)}</span>
            </span>
            <input
              type="time"
              aria-label={text('timeLabel')}
              value={custom.time}
              onChange={(event) => setCustom((current) => ({ ...current, time: event.target.value }))}
              className="rounded-md border border-gray-200 px-2 py-1.5 text-sm text-gray-900 focus:border-yellow-500 focus:outline-none focus:ring-0"
            />
          </label>
          {past && <p className="px-1 text-xs text-red-600">{text('schedulePast')}</p>}
          <div className="flex justify-end gap-2">
            <Button color="SECONDARY" size="SM" onClick={() => setCustom(null)}>
              {text('back')}
            </Button>
            <Button
              color="PRIMARY"
              size="SM"
              disabled={!chosen || past}
              onClick={() => onPick(chosen)}
            >
              {text('scheduleCta')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulePopover;
